import React, { Children, cloneElement, useState, useRef, useEffect } from 'react-x';
import propTypes from 'prop-types-x';
import classNames from 'classnames';
import { CssAnimation } from './CssAnimation.jsx';
import { useUpdateEffect } from '../hooks/useUpdateEffect.js';
import { useEventListener } from '../hooks/useEventListener.js';
import { useSwipe } from '../hooks/useSwipe.js';
import { useObserver } from '../hooks/useObserver.js';
import { multiplier } from '../../utilities/multiplier.js';
import { getKeyboardFocusableElements } from '../../utilities/getKeyboardFocusableElements.js';
import { randomString } from '../../utilities/randomString.js';
import { formatDoubleDigit } from '../../utilities/formatDoubleDigit.js';
import { CORE_GTM_TRACKING_VALUES } from '../../constants/analytics.js';
import { fireGTMTrackingEvent } from '../../utilities/fireGTMTrackingEvent.js';

export function Carousel(props) {

  const { 
    children, 
    centered, 
    infinite, 
    pagination, 
    uniformHeight, 
    cssTransition,
    gtmComponentGroup, 
    gtmComponentName,
    gtmAction,
    gtmLabel
  } = props;

  const block = 'c-carousel';
  const numberOfCarouselItems = children.length;
  const carouselWrapperRef = useRef();
  const carouselSliderRef = useRef();
  const carouselItemRef = useRef();
  const transitionEnabledRef = useRef(true);
  const inTransitionRef = useRef(false);
  const clickedDirectionRef = useRef();
  const deferedMoveCarousel = useRef(false);
  const [wrapperWidth, setWrapperWidth] = useState(0);
  const [itemWidth, setItemWidth] = useState(0);
  const [activeIndex, setActiveIndex] = useState();
  const [lastItemOffset, setLastItemOffset] = useState(0);
  const [centeredOffset, setCenteredOffset] = useState(0);
  const [isInteractive, setIsInteractive] = useState(false);
  const [reachedEndOfItems, setReachedEndOfItems] = useState(false);
  const [resetInfinite, setResetInfinite] = useState(false);
  const [page, setPage] = useState(1);
  const sliderTransform = (activeIndex * -itemWidth) + centeredOffset + lastItemOffset;

  useEffect(() => {
    setDimensions();
  }, []);

  useEventListener('resize', setDimensions);

  useUpdateEffect(() => {
    resetCarousel();
  }, [itemWidth, wrapperWidth]);
  
  useObserver({
      root: carouselWrapperRef.current,
      target: `${block}__item`,
    },
    params => {
      getKeyboardFocusableElements(params.element)
      .forEach(el => {
        el.tabIndex = params.status === 'in-view' ? 0 : -1;
      });
  });

  useUpdateEffect(() => {
    inTransitionRef.current = false;
    moveCarousel(clickedDirectionRef.current);
  }, [resetInfinite]);

  useUpdateEffect(() => {
    console.log(gtmLabel)
    if (isInteractive) {
      fireGTMTrackingEvent({
        name: CORE_GTM_TRACKING_VALUES.name.componentEvent,
        componentName: gtmComponentName,
        componentGroup: gtmComponentGroup,
        action: gtmAction,
        label: gtmLabel || gtmLabel == '' ? gtmLabel : page
      });
    }
  }, [activeIndex]);

  function setDimensions() {
    if (carouselWrapperRef.current && carouselItemRef.current) {
      const wrapperWidth = carouselWrapperRef.current.offsetWidth;
      const itemWidth = carouselItemRef.current.offsetWidth;
      const centeredOffset = centered ? wrapperWidth/2 - itemWidth/2 : 0;
      setCenteredOffset(centeredOffset);
      setItemWidth(itemWidth);
      setWrapperWidth(wrapperWidth);
    }
  };

  function resetCarousel(resetInfinite=false, resetInfiniteTo='first') {
    transitionEnabledRef.current = false;
    const activeIndex = !infinite ? 0 : resetInfiniteTo === 'first' ? numberOfCarouselItems : (numberOfCarouselItems * 2) - 1;
    setLastItemOffset(0);
    setReachedEndOfItems(false);
    setActiveIndex(activeIndex);

    if (resetInfinite) {
      // timeout to ensure the reset runs first due to some quirks on mobile browsers
      setTimeout(() => { 
        setResetInfinite(randomString());
      }, 0);
    }
  };

  function moveCarousel(direction) {
    clickedDirectionRef.current = direction;

    if (infinite && (activeIndex === numberOfCarouselItems * 2)) {
      if (inTransitionRef.current) {
        deferedMoveCarousel.current = true;
        return;
      }
      resetCarousel(true, 'first');
    } 
    else if (infinite && (activeIndex === numberOfCarouselItems - 1)) {
      if (inTransitionRef.current) {
        deferedMoveCarousel.current = true;
        return;
      }
      resetCarousel(true, 'last');
    } 
    else {
      // lastItemOffset - this value is used to ensure the last item or the carousel does not
      // move further than the right edge of the carousel wrapper when the carousel is not in
      // infinite mode.
      const combinedWidthOfCurrentAndNextItems = (numberOfCarouselItems - activeIndex) * itemWidth;
      const maxSliderTravel = combinedWidthOfCurrentAndNextItems - (wrapperWidth - centeredOffset);
      const isEndOfSliderTrack = direction === 'next' && maxSliderTravel < itemWidth;
      const lastItemOffset = isEndOfSliderTrack && !infinite ? itemWidth - maxSliderTravel : 0;
  
      transitionEnabledRef.current = true;
      inTransitionRef.current = true;
      setLastItemOffset(lastItemOffset);
      setReachedEndOfItems(isEndOfSliderTrack);
      if (direction === 'next') {
        setActiveIndex(prevValue => prevValue + 1);
        setPage(prevValue => prevValue + 1 > numberOfCarouselItems ? 1 : prevValue + 1);
      } else {
        setActiveIndex(prevValue =>  prevValue - 1);
        setPage(prevValue => prevValue - 1 < 1 ? numberOfCarouselItems : prevValue - 1);
      }
    }
  };

  function handleTransitionEnd() {
    inTransitionRef.current = false;
    if (deferedMoveCarousel.current) {
      moveCarousel(clickedDirectionRef.current);
      deferedMoveCarousel.current = false;
    }
  };

  const swipeEvents = useSwipe((swipeRight, swipeLeft) => {
    if (swipeLeft) {
      if (activeIndex !== 0 || infinite) {
        moveCarousel('previous');
      }
    }
    else if (swipeRight) {
      if (!reachedEndOfItems || infinite) {
        moveCarousel('next');
      }
    }
  }, {
    mouse: false
  });

  const carouselControl = ({ index, direction, d, viewBox, disable }) => (
    <CssAnimation
    enter={{ name: 'long-slide-left-fade-in', delay: `${multiplier(index, 0.25)}, hidden` }}
    triggerEnter={'in-view-init'}
    >
      <button className={ 
        classNames({
        [`${block}__control animation-trigger`]: true,
        'control-disabled': disable
      })}
      aria-label={`scroll-$direction`}
      onClick={() => moveCarousel(direction)}
      >
        <svg className={`${block}__control-circle`} viewBox='0 0 48 48'>
          <circle cx='24' cy='24' r='22' strokeWidth='2px'></circle>
        </svg>
        <svg className={`${block}__control-icon`} xmlns='http://www.w3.org/2000/svg' width='48' height='18' viewBox={viewBox} fill='none'>
          <path d={d} strokeWidth='2' strokeMiterlimit='10' strokeLinecap='round'></path>
        </svg>
      </button>
    </CssAnimation>
  );

  const renderChildren = () => 
    Children.map(children, (child) => 
      <div 
      ref={carouselItemRef} 
      className={classNames({
        [`${block}__item`]: true,
        [`${block}__item--uniform-height`]: uniformHeight
      })}
      >
        { cloneElement(child, { cssAnimationTrigger: isInteractive }) }
      </div>
    );
  
  return (
      <div className={`${block}__carousel`}>
        <div className={`${block}__wrapper`} ref={carouselWrapperRef}>
          <div className='row'>
            <div className={ 
              classNames({
              [`${block}__controls`]: true,
              [`${block}__controls--centered`]: centered,
              [`${block}__controls--pagination`]: pagination,
              'col-12 col-lg-10 offset-lg-1': true
            })}
            >
              { 
                carouselControl({ 
                  index: 0,
                  direction: 'previous', 
                  d: 'M12.75 1.5L5.25 9L12.75 16.5', 
                  viewBox: '0 0 20 18',
                  disable: !infinite && activeIndex === 0
                })
              }
              {
                pagination && 
                  <CssAnimation 
                  enter={{ name: 'slide-up-fade-in', duration: 0.5, delay: '0.75, hidden', easing: 'ease-out' }} 
                  triggerEnter={'in-view-init'}
                  >
                    <div className={`${block}__pagination`}>
                      <div className='list-item'>{ formatDoubleDigit(page) }</div> 
                      <div className='list-item'>{ formatDoubleDigit(numberOfCarouselItems) }</div>
                    </div>
                  </CssAnimation>
              }
              { 
                carouselControl({ 
                  index: 1,
                  direction: 'next', 
                  d: 'M5.25 1.5L12.75 9L5.25 16.5', 
                  viewBox: '0 0 16 18',
                  disable: !infinite && ((activeIndex === numberOfCarouselItems - 1) || reachedEndOfItems)
                }) 
              }
            </div>
          </div>
          <CssAnimation 
          enter={{ name: 'short-slide-up-fade-in', duration: 0.15, easing: 'ease-out' }} 
          triggerEnter={'in-view-init'}
          onEnterEnd={() => setIsInteractive(true)}
          inViewProps={{ threshold: 0.2 }}
          >
            <div>
            <div 
            className={`${block}__slider`} 
            ref={carouselSliderRef}
            style={{ 
              transform: `translate3d(${sliderTransform}px, 0, 0)`,
              transition: !transitionEnabledRef.current ? 'none' : cssTransition
            }}
            onTransitionEnd={handleTransitionEnd}
            { ...swipeEvents }
            >
              { infinite && renderChildren() }
              { renderChildren() }
              { infinite && renderChildren() }
            </div>
            </div>
          </CssAnimation>
        </div>
      </div>
  )
};

Carousel.propTypes = {
  children: propTypes.oneOfType([
    propTypes.arrayOf(propTypes.node),
    propTypes.node
  ]).isRequired,
  centered: propTypes.bool,
  infinite: propTypes.bool,
  pagination: propTypes.bool,
  cssTransition: propTypes.string,
  uniformHeight: propTypes.bool,
  gtmComponentGroup: propTypes.string, 
  gtmComponentName: propTypes.string,
  gtmAction: propTypes.string,
  gtmLabel: propTypes.string
};

Carousel.defaultProps = {
  centered: false,
  infinite: false,
  pagination: false,
  uniformHeight: true,
  gtmComponentName: CORE_GTM_TRACKING_VALUES.componentName.carousel,
  gtmComponentGroup: CORE_GTM_TRACKING_VALUES.componentGroup.campaigns,
  gtmAction: CORE_GTM_TRACKING_VALUES.action.browseContent
};